import { useMemo } from 'react';
import type { ComplianceResponse } from '@/api/dataVault';
import { GlowBadge } from '../components/ui/GlowBadge';
import { SkeletonTable } from '../components/ui/SkeletonLoader';

interface Props {
  data: ComplianceResponse | undefined;
  isLoading: boolean;
  page: number;
  onPageChange: (p: number) => void;
}

function SamplingBadge({ days }: { days: number | null | undefined }) {
  if (days === null || days === undefined) return <span className="text-mono-500">--</span>;
  const variant = days > 365 ? 'danger' as const : days > 180 ? 'warning' as const : 'success' as const;
  return <GlowBadge variant={variant}>{days}d AGO</GlowBadge>;
}

function CompletenessBadge({ pct }: { pct: number | null | undefined }) {
  if (pct === null || pct === undefined) return <span className="text-mono-500">--</span>;
  const color = pct >= 90 ? 'text-emerald-400'
    : pct >= 60 ? 'text-amber-400'
    : 'text-red-400';
  return <span className={`text-xs font-medium ${color}`}>{pct}%</span>;
}

export function ComplianceTab({ data, isLoading, page, onPageChange }: Props) {
  if (isLoading && !data) {
    return (
      <div className="animate-fade-in">
        <SkeletonTable rows={8} />
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-mono-400 text-xs tracking-wider">
          {data?.total === 0 ? 'NO COMPLIANCE DATA AVAILABLE' : 'SELECT SITES TO VIEW COMPLIANCE DATA'}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3" style={{ borderBottom: '1px solid transparent', borderImage: 'linear-gradient(to right, rgba(6,182,212,0.3), rgba(168,85,247,0.3)) 1' }}>
        <div className="text-xs text-mono-200 tracking-wider pb-2">
          {data.total} SITES
        </div>
      </div>

      <div className="border border-mono-800 rounded overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-mono-800 bg-mono-900/50">
              <th className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium">SITE</th>
              <th className="px-3 py-2.5 text-right text-xs text-mono-300 tracking-widest font-medium">RESULTS</th>
              <th className="px-3 py-2.5 text-right text-xs text-mono-300 tracking-widest font-medium">EXCEEDANCES</th>
              <th className="px-3 py-2.5 text-right text-xs text-mono-300 tracking-widest font-medium">EXCEED %</th>
              <th className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium">ANALYTES EXCEEDING</th>
              <th className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium">LAST SAMPLE</th>
              <th className="px-3 py-2.5 text-right text-xs text-mono-300 tracking-widest font-medium">WELLS</th>
              <th className="px-3 py-2.5 text-right text-xs text-mono-300 tracking-widest font-medium">SAMPLED (1YR)</th>
              <th className="px-3 py-2.5 text-center text-xs text-mono-300 tracking-widest font-medium">COMPLETENESS</th>
              <th className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium">STANDARDS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-mono-800/50">
            {data.items.map((row) => (
              <tr key={row.site_id} className="hover:bg-mono-800/30 transition-colors">
                <td className="px-3 py-2 text-xs text-mono-100 max-w-[200px] truncate">{row.site_name}</td>
                <td className="px-3 py-2 text-xs text-mono-200 text-right">{row.total_results.toLocaleString()}</td>
                <td className="px-3 py-2 text-xs text-right">
                  {row.exceedance_count > 0 ? (
                    <span className="text-red-400 font-medium">{row.exceedance_count.toLocaleString()}</span>
                  ) : (
                    <span className="text-emerald-400">0</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-right">
                  {row.exceedance_pct !== null ? (
                    <span className={row.exceedance_pct > 10 ? 'text-red-400' : row.exceedance_pct > 0 ? 'text-amber-400' : 'text-emerald-400'}>
                      {row.exceedance_pct}%
                    </span>
                  ) : '--'}
                </td>
                <td className="px-3 py-2 text-xs text-mono-200 max-w-[250px] truncate">
                  {row.analytes_exceeding.length > 0
                    ? row.analytes_exceeding.join(', ')
                    : <span className="text-mono-500">none</span>}
                </td>
                <td className="px-3 py-2 text-xs">
                  <SamplingBadge days={row.days_since_last_sample} />
                </td>
                <td className="px-3 py-2 text-xs text-mono-200 text-right">{row.total_wells}</td>
                <td className="px-3 py-2 text-xs text-mono-200 text-right">{row.wells_sampled_last_year}</td>
                <td className="px-3 py-2 text-center">
                  <CompletenessBadge pct={row.sampling_completeness_pct} />
                </td>
                <td className="px-3 py-2">
                  {row.enabled_standards?.map((std) => (
                    <span
                      key={std}
                      className="inline-block px-1.5 py-0.5 text-[10px] bg-mono-800 text-mono-400 rounded mr-1 tracking-wider"
                    >
                      {std.replace(/_/g, ' ')}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.total_pages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-mono-800">
          <span className="text-xs text-mono-200 tracking-wider">
            PAGE {data.page} OF {data.total_pages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="px-3 py-1 text-xs tracking-wider text-mono-200 hover:text-mono-100 bg-mono-800 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              PREV
            </button>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= data.total_pages}
              className="px-3 py-1 text-xs tracking-wider text-mono-200 hover:text-mono-100 bg-mono-800 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              NEXT
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
